/**
 * Bulk Import Button Component
 *
 * Main orchestrator component that manages the bulk import workflow.
 * Coordinates between file upload, column mapping, and progress tracking.
 */

import React, { useState, useEffect } from 'react';
import { Button, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import ExcelUploadModal from './ExcelUploadModal';
import ColumnMappingModal from './ColumnMappingModal';
import ImportProgressModal from './ImportProgressModal';
import bulkImportService from '../../../services/bulkImportService';
import axiosClient from '../../../lib/http/axiosClient';

const BulkImportButton = ({
  tableId,
  tableName,
  onImportComplete,
  buttonText = 'Bulk Import',
  buttonType = 'default',
  buttonSize = 'middle'
}) => {
  const [step, setStep] = useState('idle'); // idle, upload, mapping, progress
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tableSchema, setTableSchema] = useState(null);  // Fetch dynamically

  // Fetch table schema dynamically from backend
  useEffect(() => {
    const fetchSchema = async () => {
      if (!tableId) return;

      try {
        const response = await axiosClient.get(`/master-data/tables/${tableId}`);
        setTableSchema(response.data);
      } catch (error) {
        console.error('Failed to load table schema:', error);
        message.error('Failed to load table schema');
      }
    };

    fetchSchema();
  }, [tableId]);

  const handleFileSelect = async (file) => {
    setLoading(true);
    try {
      // Call preview API to analyze file and get column suggestions
      const preview = await bulkImportService.previewImport(tableId, file);

      setPreviewData(preview);
      setUploadedFile(file);
      setStep('mapping');

      message.success('File preview loaded successfully');
    } catch (error) {
      console.error('Preview failed:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to preview file';
      message.error(errorMessage);
      setStep('idle');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmMapping = async (columnMapping) => {
    setLoading(true);
    try {
      // Start import job with column mapping
      const job = await bulkImportService.startImport(
        tableId,
        uploadedFile,
        columnMapping,
        {
          skipHeader: true,
          ignoreErrors: false // Stop on first error by default
        }
      );

      setJobId(job.job_id);
      setStep('progress');

      message.success('Import started successfully');
    } catch (error) {
      console.error('Import failed:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to start import';
      message.error(errorMessage);
      setStep('idle');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('idle');
    setUploadedFile(null);
    setPreviewData(null);
    setJobId(null);
    setLoading(false);

    // Refresh parent table data if callback provided
    if (onImportComplete) {
      onImportComplete();
    }
  };

  const handleCancelUpload = () => {
    setStep('idle');
    setUploadedFile(null);
    setPreviewData(null);
    setLoading(false);
  };

  const handleCancelMapping = () => {
    setStep('upload');
    setPreviewData(null);
  };

  return (
    <>
      <Button
        type={buttonType}
        icon={<UploadOutlined />}
        onClick={() => setStep('upload')}
        size={buttonSize}
        disabled={loading || !tableSchema}
        loading={!tableSchema}
      >
        {buttonText}
      </Button>

      <ExcelUploadModal
        visible={step === 'upload'}
        onClose={handleCancelUpload}
        onFileSelect={handleFileSelect}
        tableId={tableId}
        tableName={tableName}
      />

      <ColumnMappingModal
        visible={step === 'mapping'}
        onClose={handleCancelMapping}
        onConfirm={handleConfirmMapping}
        previewData={previewData}
        tableSchema={tableSchema}
        tableName={tableName}
      />

      <ImportProgressModal
        visible={step === 'progress'}
        onClose={handleClose}
        jobId={jobId}
        fetchProgress={bulkImportService.getJobStatus}
      />
    </>
  );
};

export default BulkImportButton;
